import { Dialog, Link } from '@mui/material';
import isEqual from 'lodash/isEqual';
import { MHMButton } from '../MHM/MHMButton';
import { MHMDialog } from '../MHM/MHMDialog';
import { MHMPasswordField } from '../MHM/MHMPasswordField';
import { MHMTypography } from '../MHM/MHMTypography';
import { AccountModal } from './AccountModal';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  companyAuthSettingsAtom,
  deleteModalAtom,
  modifyAccountModalAtom,
  selectedAccountStateAtom,
  userAccountAtom,
} from '../../../models/atoms/account_atoms';
import { useTranslation } from '../../../contexts/TranslationContext';
import {
  AccountModalPermissions,
  AccountPermissions,
} from './AccountModalPermissions';
import { account_controller } from '../../../controllers/account_controller';
import { ChangeEvent, useEffect, useState } from 'react';
import { validatePassword } from '../../../utils/account_validations';

export function ModifyAccountModal() {
  const { t } = useTranslation();
  const [modifyAccountModal, setModifyAccountModal] = useRecoilState(
    modifyAccountModalAtom,
  );
  const setDeleteModal = useSetRecoilState(deleteModalAtom);
  const selectedAccount = useRecoilValue(selectedAccountStateAtom);
  const currentAccount = useRecoilValue(userAccountAtom);
  const [permissions, setPermissions] = useState<AccountPermissions>({
    view: false,
    install: false,
    admin: false,
  });
  const [password, setPassword] = useState('');
  const [passwordValidationError, setPasswordValidationError] = useState('');

  const companyAuthSettings = useRecoilValue(companyAuthSettingsAtom);

  function closeModal() {
    setModifyAccountModal(false);
  }

  function handlePermissionsChange() {
    if (!selectedAccount) {
      return;
    }

    account_controller.modify_account(
      selectedAccount.id,
      permissions.view,
      permissions.install,
      permissions.admin,
      permissions.admin,
    );

    setModifyAccountModal(false);
  }

  function handleDelete() {
    if (!selectedAccount) {
      return;
    }

    setModifyAccountModal(false);
    setDeleteModal(true);
  }

  function handlePasswordChange(e: ChangeEvent<HTMLInputElement>) {
    if (companyAuthSettings?.auth_type === 'sso') {
      return;
    }
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordValidationError(validatePassword(newPassword) ?? '');
  }

  function handleSavePassword() {
    if (
      !selectedAccount ||
      passwordValidationError ||
      companyAuthSettings?.auth_type === 'sso'
    ) {
      return;
    }

    account_controller.change_account_password(selectedAccount.id, password);
    setModifyAccountModal(false);
  }

  useEffect(() => {
    if (!selectedAccount) {
      return;
    }

    setPermissions({
      view: selectedAccount.permissions.view_devices,
      install: selectedAccount.permissions.register_devices,
      admin: selectedAccount.permissions.manage_accounts,
    });
  }, [selectedAccount]);

  if (!selectedAccount) {
    return null;
  }

  const arePermissionsChanged = !isEqual(permissions, {
    view: selectedAccount.permissions.view_devices,
    install: selectedAccount.permissions.register_devices,
    admin: selectedAccount.permissions.manage_accounts,
  });
  const arePermissionsEmpty =
    !permissions.view && !permissions.install && !permissions.admin;

  return (
    <Dialog open={modifyAccountModal} onClose={closeModal}>
      <MHMDialog.Header onClose={closeModal}>
        {t('Modify Account')}
      </MHMDialog.Header>

      <MHMDialog.Content dividers>
        <AccountModal.FormGroup>
          <AccountModal.FormLabel
            label={t('Name')}
            labelProps={{ fontSize: 20 }}
          >
            <MHMTypography>
              {selectedAccount.first_name}
              &nbsp;
              {selectedAccount.last_name}
            </MHMTypography>
          </AccountModal.FormLabel>

          <AccountModal.FormLabel
            label={t('Email')}
            labelProps={{ fontSize: 20 }}
          >
            <MHMTypography>{selectedAccount.email}</MHMTypography>
          </AccountModal.FormLabel>

          <AccountModal.FormLabel
            label={t('Phone Number')}
            labelProps={{ fontSize: 20 }}
          >
            <MHMTypography>
              {selectedAccount.phone_number === '0'
                ? 'Na'
                : selectedAccount.phone_number}
            </MHMTypography>
          </AccountModal.FormLabel>
        </AccountModal.FormGroup>

        <AccountModalPermissions
          label={t('Change Permissions')}
          permissions={permissions}
          sso_permissions={selectedAccount.sso_permissions}
          onPermissionsChange={setPermissions}
          disabled={selectedAccount.id === currentAccount?.id}
        />

        <AccountModal.FormGroup>
          <MHMButton
            sx={{ width: '5rem' }}
            disabled={!arePermissionsChanged || arePermissionsEmpty}
            onClick={handlePermissionsChange}
          >
            {t('Save')}
          </MHMButton>

          <MHMButton
            color="error"
            sx={{ width: '5rem' }}
            onClick={handleDelete}
            disabled={selectedAccount.id === currentAccount?.id}
          >
            {t('Delete')}
          </MHMButton>
        </AccountModal.FormGroup>

        {companyAuthSettings?.auth_type === 'sso' ? null : (
          <AccountModal.FormGroup>
            <AccountModal.FormLabel
              label={t('Change Password')}
              labelProps={{ fontSize: 20 }}
            >
              <MHMPasswordField
                label={t('New Password')}
                autoComplete="off"
                fullWidth
                required
                value={password}
                onChange={handlePasswordChange}
                error={Boolean(passwordValidationError)}
                helperText={
                  <>
                    {passwordValidationError ? (
                      <>
                        {t(passwordValidationError)}
                        <br />
                      </>
                    ) : null}
                    <Link href="https://device-service.manhole-metrics.com/redirect-to-password-policy">
                      ({t('View our Password Policy')})
                    </Link>
                  </>
                }
              />
            </AccountModal.FormLabel>
            <MHMButton
              sx={{ width: '5rem' }}
              disabled={Boolean(passwordValidationError) || !password}
              onClick={handleSavePassword}
            >
              {t('Save')}
            </MHMButton>
          </AccountModal.FormGroup>
        )}
      </MHMDialog.Content>
    </Dialog>
  );
}
